import React, { useEffect, useMemo, useState } from "react";
import FileUpload from "components/UI/FileUpload";
import { axiosCitajteo } from "common/api/axios/citajteo_axios";
import { FILE_UP_NAME } from "constant/file/file_constants";
import styled from "styled-components";
import SeriesPhotoFileNames from "./SeriesPhotoFileNames";
import { splitPath } from "common/util/file/file_common";
import { PhotoSeries } from "model/photo_series/PhotoSeries";
import { PhotoInfo } from "model/photo_series/PhotoInfo";
import SeriesPhotoFileLink from "./SeriesPhotoFileLink";

type SeriesPhotoFileEditProps = {
  photoSer: PhotoSeries;
};

const SeriesPhotoFileEdit = ({ photoSer }: SeriesPhotoFileEditProps) => {
  const initialFoldName = useMemo(() => photoSer.fold, [photoSer]);
  const [fileNames, setFileNames] = useState<PhotoInfo[]>(photoSer.photos);
  const [foldName, setFoldName] = useState<string>(initialFoldName);
  const [showUpdate, setShowUpdate] = useState<boolean>(false);

  useEffect(() => {
    const fileNamesChanged =
      JSON.stringify(fileNames) !== JSON.stringify(photoSer.photos);

    if (fileNamesChanged && !showUpdate) {
      setShowUpdate(true);
    }
  }, [fileNames, photoSer.photos, showUpdate]);

  const fileUploadIndHandle = (data: any, ind: number) => {
    // console.log("fileUploadIndHandle", data, ind);
    const photoFileName = splitPath(data.files[0].name);
    if (photoFileName) {
      const uploadedFoldName = data.photoSeriesInfo.fold;
      if (uploadedFoldName !== foldName) {
        setFoldName(uploadedFoldName);
      }
    }
    const newFileNames = data.files.map(
      (photoInfo: PhotoInfo) => new PhotoInfo(photoInfo),
    );
    const fNames = [
      ...fileNames.slice(0, ind),
      ...newFileNames,
      ...fileNames.slice(ind),
    ];
    setFileNames(fNames);
  };

  const photoSeriesApiUpUrl = `photo-series/${photoSer.id}/photo/file`;

  const resetFileNames = () => {
    setFileNames(photoSer.photos);
    setShowUpdate(false);
  };

  const removeFileName = (fileNameIndex: number) => {
    setFileNames((currentFileNames) =>
      currentFileNames.filter((_, index) => index !== fileNameIndex),
    );
  };

  return (
    <Div>
      <div>Foto</div>
      <FileNamesNavDiv>
        {fileNames.length > 0 && (
          <>
            {fileNames.map((fileName, index) => (
              <React.Fragment key={`${fileName}-${index}`}>
                <FileUpload
                  axiosInstance={axiosCitajteo}
                  fileFieldName={FILE_UP_NAME}
                  fileUploadHandle={(data: any) =>
                    fileUploadIndHandle(data, index)
                  }
                  uploadUrl={photoSeriesApiUpUrl}
                  multiple
                  upLabel=""
                />
                <FileNameNavDiv>
                  <SeriesPhotoFileLink
                    fileName={fileName}
                    foldName={foldName}
                  />
                  <RemoveFileNameButton
                    type="button"
                    onClick={() => removeFileName(index)}
                    aria-label={`Obriši fotografiju ${fileName.name}`}
                  >
                    ×
                  </RemoveFileNameButton>
                </FileNameNavDiv>
              </React.Fragment>
            ))}
          </>
        )}
        <FileUpload
          axiosInstance={axiosCitajteo}
          fileFieldName={FILE_UP_NAME}
          fileUploadHandle={(data: any) =>
            fileUploadIndHandle(data, fileNames.length)
          }
          uploadUrl={photoSeriesApiUpUrl}
          multiple
          upLabel=""
        />
      </FileNamesNavDiv>

      {showUpdate && (
        <SeriesPhotoFileNames
          photoSerId={photoSer.id}
          fileNames={fileNames}
          setFileNames={setFileNames}
          resetFileNames={resetFileNames}
        />
      )}
    </Div>
  );
};

const Div = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FileNamesNavDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FileNameNavDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  &:hover {
    opacity: 0.75;
  }
`;

const RemoveFileNameButton = styled.button`
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0;
`;

export default SeriesPhotoFileEdit;
